import { AppModal, Button, Form } from "@/components/ui";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import type { MatterStatusGroup } from "@/lib/data/static/matterStatics";
import { useState } from "react";
import { useForm } from "react-hook-form";

type LeadConversionModalProps = {
  currentEntityLabel: string;
  isPending: boolean;
  onConfirm: (statusId: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  statusGroups: MatterStatusGroup[];
  target: "lead" | "matter";
};

export function LeadConversionModal({
  currentEntityLabel,
  isPending,
  onConfirm,
  onOpenChange,
  open,
  statusGroups,
  target,
}: LeadConversionModalProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const form = useForm({ defaultValues: { statusId: "" } });
  const statusId = form.watch("statusId");
  const targetLabel = target === "matter" ? "Matter" : "Lead";
  const setOpen = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      form.reset();
      setStatusDropdownOpen(false);
    }
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={`Convert to ${target}`}
      description={`Choose the ${target} status. Any unsaved ${currentEntityLabel} changes will be saved during conversion.`}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-neutral-900"
            disabled={!statusId || isPending}
            onClick={() => {
              void onConfirm(statusId);
            }}
          >
            {isPending ? "Converting…" : `Convert to ${target}`}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <MultiCategorySelectFormField
            label={`${targetLabel} status`}
            register={form.register("statusId")}
            items={statusGroups}
            placeholder={`Select a ${target} status`}
            searchPlaceholder="Search statuses..."
            emptyMessage="No status found."
            open={statusDropdownOpen}
            setOpen={setStatusDropdownOpen}
            disabled={isPending}
          />
        </form>
      </Form>
    </AppModal>
  );
}
